import axiosClient from "utils/axios";
import { ApiList, ApiResponse } from "../../../types/api.types";
import type {
  CancelExpectationRequest,
  CreatePaymentExpectationRequest,
  PaymentExpectation,
  RecordReceiptRequest,
  UpdatePaymentExpectationRequest,
} from "../types/payment-expectation.types";

const BASE = "/payment-expectations";

export type PaymentExpectationListParams = {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
  sourceType?: string;
  clientId?: number;
  sourceId?: number;
  expectedDateFrom?: string;
  expectedDateTo?: string;
};

export interface PaymentExpectationSummaryGroup {
  count: number;
  totalBalance: number;
  items: PaymentExpectation[];
}

export interface PaymentExpectationSummary {
  upcomingNext7Days: PaymentExpectationSummaryGroup;
  upcomingNext30Days: PaymentExpectationSummaryGroup;
  overdue: PaymentExpectationSummaryGroup;
}

function buildListParams(
  input: PaymentExpectationListParams,
): Record<string, string | number> {
  const p: Record<string, string | number> = {};
  if (input.page != null) p.page = input.page;
  if (input.limit != null) p.limit = input.limit;
  if (input.search?.trim()) p.search = input.search.trim();
  if (input.status?.trim()) p.status = input.status.trim();
  if (input.sourceType?.trim()) p.sourceType = input.sourceType.trim();
  if (input.clientId && input.clientId > 0) p.clientId = input.clientId;
  if (input.sourceId && input.sourceId > 0) p.sourceId = input.sourceId;
  if (input.expectedDateFrom?.trim()) p.expectedDateFrom = input.expectedDateFrom.trim();
  if (input.expectedDateTo?.trim()) p.expectedDateTo = input.expectedDateTo.trim();
  return p;
}

export const getPaymentExpectations = async (
  input: PaymentExpectationListParams = {},
) => {
  const response = await axiosClient.get<
    ApiResponse<ApiList<PaymentExpectation[]>>
  >(BASE, { params: buildListParams(input) });
  return response.data;
};

export const getPaymentExpectation = async (id: number) => {
  const response = await axiosClient.get<ApiResponse<PaymentExpectation>>(
    `${BASE}/${id}`,
  );
  return response.data;
};

export const getPaymentExpectationSummary = async (params?: {
  expectedDateFrom?: string;
  expectedDateTo?: string;
}) => {
  const response = await axiosClient.get<
    ApiResponse<PaymentExpectationSummary>
  >(`${BASE}/summary`, { params });
  return response.data;
};

export const createPaymentExpectation = async (
  payload: CreatePaymentExpectationRequest,
) => {
  const response = await axiosClient.post<ApiResponse<PaymentExpectation>>(
    BASE,
    payload,
  );
  return response.data;
};

export const updatePaymentExpectation = async (
  id: number,
  payload: UpdatePaymentExpectationRequest,
) => {
  const response = await axiosClient.patch<ApiResponse<PaymentExpectation>>(
    `${BASE}/${id}`,
    payload,
  );
  return response.data;
};

export const recordPaymentExpectationReceipt = async (
  id: number,
  payload: RecordReceiptRequest,
) => {
  const response = await axiosClient.post<ApiResponse<PaymentExpectation>>(
    `${BASE}/${id}/record-receipt`,
    payload,
  );
  return response.data;
};

export const cancelPaymentExpectation = async (
  id: number,
  payload?: CancelExpectationRequest,
) => {
  const response = await axiosClient.post<ApiResponse<PaymentExpectation>>(
    `${BASE}/${id}/cancel`,
    payload ?? {},
  );
  return response.data;
};
